import { FormEvent, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SigninApi } from "../../services/api/auth";
import { getApiErrorData, getApiErrorMessage } from "../../utils/api/getApiErrorMessage";
import apiClient from "../../services/api/apiClient";
import { tokenStorage } from "../../services/auth/tokenStorage";
import styles from "./DesktopAuthPage.module.scss";

export function DesktopAuthPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session");
  const state = searchParams.get("state");

  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!sessionId || !state) {
      return;
    }

    sessionStorage.setItem("desktopAuthSession", JSON.stringify({ sessionId, state }));
  }, [sessionId, state]);

  const handleYandex = () => {
    window.location.href = "/oauth2/authorization/yandex";
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    if (!sessionId || !state) {
      setError("Некорректная ссылка авторизации: отсутствуют session/state.");
      return;
    }

    if (!login.trim()) {
      setError("Укажите почту или ник.");
      return;
    }

    if (challengeId && !/^\d{6}$/.test(code)) {
      setError("Введите шестизначный код из письма.");
      return;
    }

    try {
      setLoading(true);
      const response = challengeId
        ? await SigninApi.verifyLoginCode(challengeId, code)
        : await SigninApi.login({ username: login.trim(), password, deviceModel: null, firebaseId: null });
      tokenStorage.setAccessToken(response.accessToken);

      const oauth = await apiClient.post("/v1/auth/desktop/oauth", {
        sessionId,
        state,
        accessToken: response.accessToken,
      });
      const redirectUrl = oauth.data.redirectUrl;

      if (!redirectUrl || !redirectUrl.startsWith("codecareer://")) {
        setError("Не удалось завершить авторизацию. Попробуйте еще раз.");
        return;
      }

      window.location.href = redirectUrl;
    } catch (requestError) {
      const data = getApiErrorData(requestError);
      if (typeof data?.challengeId === "string" && data.challengeId) {
        setChallengeId(data.challengeId);
        setError("Мы отправили одноразовый код на вашу почту.");
      } else {
        setError(getApiErrorMessage(requestError, "Не удалось войти. Проверьте данные."));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!sessionId || !state) {
    return (
      <div className={styles.root}>
        <h1 className={styles.title}>Вход в desktop-приложение</h1>
        <p className={styles.subtitle}>Некорректная ссылка авторизации: отсутствуют session/state.</p>
      </div>
    );
  }

  return (
    <div className={styles.root}>
      <h1 className={styles.title}>Вход в desktop-приложение</h1>
      <p className={styles.subtitle}>Войдите по почте или нику и паролю, либо через Яндекс.</p>

      <form className={styles.form} onSubmit={(event) => void handleSubmit(event)}>
        <label className={styles.field}>
          Почта или ник
          <input
            className={styles.input}
            autoComplete="username"
            value={login}
            onChange={(event) => setLogin(event.target.value)}
            placeholder="ivan@yandex.ru или nik"
          />
        </label>

        <label className={styles.field}>
          {challengeId ? "Одноразовый код" : "Пароль"}
          {challengeId ? (
            <input className={styles.input} inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))} placeholder="123456" />
          ) : (
            <span className={styles.passwordField}>
              <input className={styles.input} autoComplete="current-password" type={showPassword ? "text" : "password"} value={password} onChange={(event) => setPassword(event.target.value)} placeholder="••••••••" />
              <button className={styles.passwordToggle} type="button" onClick={() => setShowPassword((visible) => !visible)}>{showPassword ? "Скрыть" : "Показать"}</button>
            </span>
          )}
        </label>

        {error ? <p className={styles.error}>{error}</p> : null}

        <button className={styles.submit} type="submit" disabled={loading}>
          {loading ? "Подождите..." : challengeId ? "Подтвердить код" : "Войти"}
        </button>
      </form>

      <div className={styles.divider}>
        <span>или</span>
      </div>

      <button className={styles.yandex} type="button" onClick={handleYandex}>
        Войти через Яндекс
      </button>
    </div>
  );
}
